// Página de Relatórios
// FASE 4.3 - Interface para relatórios avançados

'use client'

import React, { useState } from 'react'
import { 
  FileText, 
  Download, 
  Calendar,
  Users,
  TrendingUp,
  DollarSign,
  Filter
} from 'lucide-react'

export default function RelatoriosPage() {
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear())
  const [loading, setLoading] = useState(false)

  const relatorios = [
    {
      id: 'previsao-anual',
      titulo: 'Relatório de Previsão Anual',
      descricao: 'Análise completa da receita anual com projeções e métricas detalhadas',
      icon: <Calendar className="w-8 h-8 text-blue-600" />,
      cor: 'blue',
      endpoint: '/api/relatorios/previsao-anual'
    },
    {
      id: 'ltv',
      titulo: 'Relatório de LTV por Cliente',
      descricao: 'Lifetime Value detalhado de cada cliente com análise de rentabilidade',
      icon: <Users className="w-8 h-8 text-green-600" />,
      cor: 'green',
      endpoint: '/api/relatorios/ltv'
    },
    {
      id: 'executivo',
      titulo: 'Relatório Executivo',
      descricao: 'Resumo executivo com KPIs principais, insights e recomendações estratégicas',
      icon: <TrendingUp className="w-8 h-8 text-purple-600" />,
      cor: 'purple',
      endpoint: '/api/relatorios/executivo'
    }
  ]

  const gerarRelatorio = async (relatorio) => {
    setLoading(true)
    try {
      const url = relatorio.id === 'previsao-anual' 
        ? `${relatorio.endpoint}?ano=${filtroAno}`
        : relatorio.endpoint

      const response = await fetch(url)
      const dados = await response.json()

      if (dados.sucesso) {
        // Criar e baixar arquivo JSON
        const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' })
        const url_download = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url_download
        a.download = `${relatorio.id}-${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url_download)
      } else {
        alert('Erro ao gerar relatório: ' + dados.erro)
      }
    } catch (error) {
      console.error('Erro:', error)
      alert('Erro ao gerar relatório')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Relatórios Avançados</h1>
              <p className="text-sm text-gray-600">Gere relatórios detalhados e análises estratégicas</p>
            </div>
            
            {/* Filtro de Ano */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={filtroAno}
                  onChange={(e) => setFiltroAno(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={2024}>2024</option>
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                  <option value={2027}>2027</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cards de Relatórios */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {relatorios.map((relatorio) => (
            <div key={relatorio.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-full bg-${relatorio.cor}-100`}>
                    {relatorio.icon}
                  </div>
                  <DollarSign className="w-5 h-5 text-gray-400" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {relatorio.titulo}
                </h3>
                
                <p className="text-sm text-gray-600 mb-6">
                  {relatorio.descricao}
                </p>
                
                <button
                  onClick={() => gerarRelatorio(relatorio)}
                  disabled={loading}
                  className={`w-full flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : `bg-${relatorio.cor}-600 text-white hover:bg-${relatorio.cor}-700`
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Gerar Relatório
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Informações Adicionais */}
        <div className="mt-12 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Sobre os Relatórios
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Previsão Anual</h3>
              <p className="text-sm text-gray-600">
                Análise completa da receita por mês, status das competências, top clientes por receita, 
                análise por serviços e projeções futuras baseadas em dados históricos.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900 mb-2">LTV por Cliente</h3>
              <p className="text-sm text-gray-600">
                Lifetime Value detalhado incluindo receita realizada, projetada, tempo de relacionamento, 
                ticket médio e frequência de pagamento por cliente.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Relatório Executivo</h3>
              <p className="text-sm text-gray-600">
                Resumo estratégico com KPIs principais, análise de crescimento, insights automatizados, 
                recomendações e alertas de risco para tomada de decisão.
              </p>
            </div>
          </div>
        </div>

        {/* Instruções de Uso */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-3">Como usar os relatórios</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Selecione o ano desejado no filtro superior para relatórios anuais</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Clique em "Gerar Relatório" para baixar os dados em formato JSON</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Os relatórios incluem dados em tempo real do Supabase</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Use os dados para análises externas ou apresentações executivas</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
